/**
 * "Download PDF" and "Share", all on the device: the printed sides are drawn
 * off screen by the same SheetFace the print page uses, then turned into a PDF
 * (local/pdf.ts). Sharing hands the PDF to the phone's or computer's own share
 * menu (Mail, Messages, AirDrop...) - it replaced "Email it to me", which needed
 * the card on our server.
 */
import { createRoot } from 'react-dom/client';
import { useEffect } from 'react';
import type { PaperStyle, PrintGuides, TemplateDocument, ZoneSymbol, ZoneText } from '@foldmycard/scene';
import { SheetFace, type InviteCode, type ShownImage } from './card-view.tsx';
import { jpegPdf, sheetsToJpegs } from './local/pdf.ts';

export interface CardContent {
  template: TemplateDocument;
  pictures: Map<number, ShownImage>;
  notes: Map<number, ZoneText>;
  /** The symbols in symbol frames (symbol.tsx). */
  symbols?: Map<number, ZoneSymbol>;
  guides: PrintGuides | null;
  paper?: PaperStyle;
  /** The whole card in black and white (ProjectDocument.mono). */
  mono?: boolean;
  /** The zones left plain (ZoneSlot.plain). */
  plain?: Set<number>;
  /** An invitation (invite.tsx): the RSVP code in the message frame. */
  invite?: InviteCode;
}

/** The card's title as a file name: only what file systems refuse is taken out. */
export const fileName = (title: string, ext: string) =>
  `${title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '').trim().slice(0, 60) || 'card'} - FoldMyCard.${ext}`;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function Sheets({ card, onMounted }: { card: CardContent; onMounted: () => void }) {
  useEffect(onMounted, [onMounted]);
  const W = card.template.paper.widthMm;
  return (
    <>
      {([1, 2] as const).map((f) => (
        <div key={f} style={{ width: 800, height: (800 * card.template.paper.heightMm) / W }}>
          <SheetFace template={card.template} pictures={card.pictures} notes={card.notes} symbols={card.symbols} paper={card.paper} mono={card.mono} plain={card.plain} selected={null} mode="clean" invite={card.invite}
            face={f} guides={card.guides?.faces.find((x) => x.face === f) ?? null} />
        </div>
      ))}
    </>
  );
}

/** Both printed sides of one card, drawn off screen and made pictures of. */
async function cardPages(card: CardContent) {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = 'position:fixed;left:-20000px;top:0;width:800px;pointer-events:none';
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    await new Promise<void>((resolve) => root.render(<Sheets card={card} onMounted={resolve} />));
    // the notes are laid out again once the fonts are in (useLayouts): let that land
    await document.fonts.ready;
    await sleep(120);
    await document.fonts.ready;
    await sleep(60);
    const svgs = [...host.querySelectorAll<SVGSVGElement>('svg.card-svg')];
    return await sheetsToJpegs(svgs, card.template.paper);
  } finally {
    root.unmount();
    host.remove();
  }
}

/** The card as a PDF file: both printed sides, with the fold guides when there are any. */
export async function makeCardPdf(card: CardContent, title: string): Promise<File> {
  const { pages, px, py } = await cardPages(card);
  return new File([jpegPdf(pages, px, py, card.template.paper, title)], fileName(title, 'pdf'), { type: 'application/pdf' });
}

/**
 * An invitation for every guest in one PDF (2026-10-21): both sides for each, each with the
 * guest's own name and RSVP code. One card at a time is drawn, so a long list stays light.
 * `onProgress(done, of)` after each.
 */
export async function makeInvitationsPdf(cards: CardContent[], title: string, onProgress?: (done: number, of: number) => void): Promise<File> {
  const all: Awaited<ReturnType<typeof cardPages>>['pages'] = [];
  let px = 0, py = 0;
  for (let i = 0; i < cards.length; i++) {
    const r = await cardPages(cards[i]);
    all.push(...r.pages);
    px = r.px; py = r.py;
    onProgress?.(i + 1, cards.length);
  }
  return new File([jpegPdf(all, px, py, cards[0].template.paper, title)], fileName(title, 'pdf'), { type: 'application/pdf' });
}

/** Save a file to the device's downloads. */
export function download(file: Blob, name: string) {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const canShareFiles = (file: File) => {
  try {
    return !!navigator.canShare?.({ files: [file] });
  } catch {
    return false;
  }
};

/**
 * Open the device's share menu with the file. 'shared' or 'cancelled'; 'needs-tap'
 * when the browser wants a fresh tap first (Safari does, once making the file
 * took a few seconds); 'unsupported' when this browser cannot share files.
 */
export async function share(file: File, title: string): Promise<'shared' | 'cancelled' | 'needs-tap' | 'unsupported'> {
  if (!canShareFiles(file)) return 'unsupported';
  try {
    await navigator.share({ files: [file], title });
    return 'shared';
  } catch (err) {
    const name = (err as Error).name;
    if (name === 'AbortError') return 'cancelled';
    if (name === 'NotAllowedError') return 'needs-tap';
    throw err;
  }
}
